import { useAuthStore } from '@common/auth-layer/store/auth.store'
import { hydrateCache } from '@common/core-layer/utils/cache-hydration.utils'
import { getHttpStatusFromUnknown } from '@common/core-layer/utils/http-status.utils'
import { isCoursesContextPath } from '@common/core-layer/utils/route-path-segments'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { useLocation } from '@common/location-layer/composables/useLocation'
import { useLogger } from '@common/core-layer/composables/useLogger'
import { useNuxtApp } from 'nuxt/app'
import { useCoursesAdminPreviewStore } from '@courses/admin-preview-layer/stores/courses-admin-preview.ui-store'
import type { LibraryProducts } from '@courses/types-layer/types/library'
import {
  coursesAccessGuardQueryKeys,
  fetchBasicProductInfo,
  fetchCourseAccessData,
  fetchLearnerLibraryProducts,
  type BasicProductInfo,
  type CourseAccessData,
} from '../services/courses-access-guard.service'
import { isLegacyCoursesLessonPath, isLegacyCoursesLibraryPath } from '../utils/legacy-course-url'
import {
  isCheckoutRouteName,
  isCoursesAdminPreviewBypass,
  isExplicitExpiredAccessError,
  isPublicCheckoutRoutePath,
  isSentinelProductId,
  shouldAllowAccessData,
  shouldAllowErrorAccess,
  shouldAllowRestrictedAccess,
} from '../utils/courses-access-guard.utils'
import type { QueryClient } from '@tanstack/vue-query'
import type { Pinia } from 'pinia'

const libraryQueryKeys = {
  list: (locId: string) => ['library', locId, 'list'] as const,
}

interface GuardLogLabels {
  path: string
  productId?: string
  decision: string
  env: string
}

function logGuardDecision(logger: ReturnType<typeof useLogger>['logger'], labels: GuardLogLabels, message: string, error?: unknown): void {
  const extras = {
    labels: {
      context: 'courses-access-guard',
      env: labels.env,
      path: labels.path,
      productId: labels.productId ?? '',
      decision: labels.decision,
    },
  }
  if (error !== undefined) {
    logger.error(message, {
      ...extras,
      error: error instanceof Error ? error : new Error(String(error)),
    })
    return
  }
  logger.info(message, extras)
}

function isPublicOrLegacyCourseRoute(path: string): boolean {
  return isPublicCheckoutRoutePath(path) || isLegacyCoursesLessonPath(path) || isLegacyCoursesLibraryPath(path)
}

type PreconditionResult = { action: 'allow' } | { action: 'redirect-home' } | { action: 'redirect-courses' } | { action: 'proceed' }

function evaluateRoutePreconditions(params: {
  matched: Array<{ path: string }>
  isAuthenticated: boolean
  isCheckoutRoute: boolean
  productId?: string
}): PreconditionResult {
  const hasCoursesRouteMatch = params.matched.some(
    record => record.path.startsWith('/courses') || record.path.includes('/learning/courses/')
  )
  if (!hasCoursesRouteMatch || (!params.isAuthenticated && !params.isCheckoutRoute)) {
    return { action: 'redirect-home' }
  }

  const isInvalidSentinel = isSentinelProductId(params.productId)
  if (isInvalidSentinel || (params.isCheckoutRoute && !params.productId)) {
    return { action: 'redirect-courses' }
  }

  if (params.isCheckoutRoute || !params.productId) {
    return { action: 'allow' }
  }

  return { action: 'proceed' }
}

async function resolveLibraryProducts(
  queryClient: QueryClient,
  locationId: string,
  cached: LibraryProducts | undefined
): Promise<LibraryProducts | undefined> {
  if (cached !== undefined) {
    return cached
  }
  try {
    const products = (await hydrateCache({
      queryClient,
      queryKey: libraryQueryKeys.list(locationId),
      fetchFn: () => fetchLearnerLibraryProducts({ locationId }),
    })) as LibraryProducts | null
    return products ?? undefined
  } catch {
    return undefined
  }
}

async function evaluateAccessGrant(params: {
  accessData: CourseAccessData | null
  productId: string
  queryClient: QueryClient
  locationId: string
  cachedLibraryProducts: LibraryProducts | undefined
}): Promise<'allow-access' | 'allow-expired' | 'allow-restricted-library' | 'redirect-hub'> {
  if (shouldAllowAccessData(params.accessData)) {
    return params.accessData?.isAccessExpired === true ? 'allow-expired' : 'allow-access'
  }

  const libraryProducts = await resolveLibraryProducts(params.queryClient, params.locationId, params.cachedLibraryProducts)

  if (shouldAllowRestrictedAccess({ productId: params.productId, libraryProducts })) {
    return 'allow-restricted-library'
  }

  return 'redirect-hub'
}

async function evaluateErrorFallback(params: {
  error: unknown
  productId: string
  queryClient?: QueryClient
  locationId?: string
  cachedLibraryProducts: LibraryProducts | undefined
}): Promise<'allow-expired' | 'allow-restricted-library' | 'redirect-hub'> {
  if (isExplicitExpiredAccessError(params.error)) {
    return 'allow-expired'
  }

  if (getHttpStatusFromUnknown(params.error) !== 403 || !params.queryClient || !params.locationId) {
    return 'redirect-hub'
  }

  const libraryProducts = await resolveLibraryProducts(params.queryClient, params.locationId, params.cachedLibraryProducts)

  if (shouldAllowErrorAccess({ error: params.error, productId: params.productId, libraryProducts })) {
    return 'allow-restricted-library'
  }

  return 'redirect-hub'
}

/**
 * Courses Access Control Middleware
 *
 * Authentication and access check for course routes.
 * Redirects unauthenticated users to home page.
 * Redirects users without access to the courses hub (`courses-back`).
 * Expired access (`isAccessExpired`) stays on the course URL so MFA/host can render.
 * Restricted access (e.g. yet-to-start with valid purchase) stays on the course URL
 * when verified by the learner's library cache, allowing the theme to render its lock state.
 */
export default defineNuxtRouteMiddleware(async to => {
  if (!isCoursesContextPath(to.path) || isPublicOrLegacyCourseRoute(to.path)) {
    return
  }

  const { logger } = useLogger()
  const { CPNavigateTo } = useCPNavigation()
  const nuxtApp = useNuxtApp()
  const env = process.server ? 'SERVER' : 'CLIENT'
  const productId = to.params.productId as string | undefined

  const log = (message: string, decision: string, error?: unknown) =>
    logGuardDecision(logger, { path: to.path, productId, decision, env }, message, error)

  const groupSlug = typeof to.params.groupSlug === 'string' ? to.params.groupSlug : undefined
  const redirectToHub = () =>
    CPNavigateTo({
      name: 'courses-back',
      ...(groupSlug ? { params: { groupSlug } } : {}),
      options: { replace: true, redirectCode: 302 },
    })

  let queryClient: QueryClient | undefined
  let locationIdVal: string | undefined
  let cachedLibraryProducts: LibraryProducts | undefined

  try {
    const pinia = nuxtApp.$pinia as Pinia
    const authStore = useAuthStore(pinia)
    const adminPreviewStore = useCoursesAdminPreviewStore(pinia)
    const ssrCookieHeader = process.server ? nuxtApp.ssrContext?.event?.node?.req?.headers?.cookie : undefined

    if (
      isCoursesAdminPreviewBypass({
        path: to.path,
        query: to.query as Record<string, unknown>,
        isAdminPreview: adminPreviewStore.isAdminPreview,
        isServer: process.server,
        ssrCookieHeader,
      })
    ) {
      return
    }

    const isCheckoutRoute = isCheckoutRouteName(to.name)
    const precondition = evaluateRoutePreconditions({
      matched: to.matched,
      isAuthenticated: authStore.isLoggedIn,
      isCheckoutRoute,
      productId,
    })

    if (precondition.action === 'redirect-home') {
      log('Blocked courses route; redirecting home', 'redirect-home')
      return CPNavigateTo({ name: 'index', options: { replace: true, redirectCode: 302 } })
    }

    if (precondition.action === 'redirect-courses') {
      log('Invalid productId; redirecting to courses', 'redirect-courses')
      return CPNavigateTo({ name: 'courses', options: { replace: true, redirectCode: 302 } })
    }

    if (precondition.action === 'allow') {
      return
    }

    queryClient = nuxtApp.$queryClient as QueryClient | undefined
    if (!queryClient) {
      return
    }

    const { locationId } = useLocation()
    locationIdVal = locationId.value
    if (!locationIdVal) {
      log('No locationId; redirecting home', 'redirect-home')
      return CPNavigateTo({ name: 'index', options: { replace: true, redirectCode: 302 } })
    }

    cachedLibraryProducts = queryClient.getQueryData<LibraryProducts>(libraryQueryKeys.list(locationIdVal))

    const basicProductInfo = (await hydrateCache({
      queryClient,
      queryKey: coursesAccessGuardQueryKeys.basicProductInfo(productId!, locationIdVal),
      fetchFn: () => fetchBasicProductInfo({ courseId: productId!, locationId: locationIdVal! }),
    })) as BasicProductInfo | null

    if (!basicProductInfo?.locationId) {
      log('Basic product info not found; redirecting to courses', 'redirect-courses')
      return CPNavigateTo({ name: 'courses', options: { replace: true, redirectCode: 302 } })
    }

    const accessData = (await hydrateCache({
      queryClient,
      queryKey: coursesAccessGuardQueryKeys.productAccess(productId!, basicProductInfo.locationId, 'communities'),
      fetchFn: () =>
        fetchCourseAccessData({
          courseId: productId!,
          locationId: basicProductInfo.locationId,
          source: 'communities',
        }),
    })) as CourseAccessData | null

    const decision = await evaluateAccessGrant({
      accessData,
      productId: productId!,
      queryClient,
      locationId: locationIdVal,
      cachedLibraryProducts,
    })

    if (decision === 'redirect-hub') {
      log('Access denied; redirecting to courses hub', 'redirect-hub')
      return redirectToHub()
    }

    log(decision === 'allow-restricted-library' ? 'Restricted access granted' : 'Access granted', decision)
  } catch (error) {
    log('Access control failed', 'catch', error)
    const fallbackDecision = await evaluateErrorFallback({
      error,
      productId: productId ?? '',
      queryClient,
      locationId: locationIdVal,
      cachedLibraryProducts,
    })
    if (fallbackDecision !== 'redirect-hub') {
      log('Error fallback allowed', fallbackDecision)
      return
    }
    return redirectToHub()
  }
})
